import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import TableContainer from "components/Common/TableContainer";
import {
  submitUserTypeFilterData,
  fetchUserTypeTableAndFilterSchema,
} from "model/UserTypeModel";
import { toast } from "react-toastify";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { DEFAULT_PAGE_SIZE, urlEndPoint } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const noop = () => {};

const UserType = () => {
  const { permissionData } = usePermissionContext() || {};
  const navigate = useNavigate();
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("UserType");
  const initialFilterFetchedRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userTypeData, setUserTypeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const defaultTableMetaData = [
    { Header: "ID", accessor: "id" },
    { Header: "Name", accessor: "name" },
    { Header: "Status", accessor: "status" },
  ];

  const getUserTypeTableSchema = async () => {
    const response = await fetchMetadataSchema("metadata:userType", () =>
      fetchUserTypeTableAndFilterSchema()
    );
    const columns = response?.data?.table?.columns || [];
    if (Array.isArray(columns) && columns.length) {
      setTableMetaData(
        columns.map((col) => ({
          Header: col?.title,
          accessor: col?.field,
          type: col?.type,
        }))
      );
      return;
    }
    setTableMetaData(defaultTableMetaData);
  };

  const filteredData = useMemo(() => userTypeData, [userTypeData]);

  const userTypeActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.UserTypeAccess),
      canEdit: Boolean(permissionData?.UserTypeAccess),
    }),
    [permissionData]
  );

  const hasAnyUserTypeRowAction = useMemo(
    () => userTypeActionPermissions.canEdit,
    [userTypeActionPermissions]
  );

  const getUserTypeList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = {
      ...requestPayload,
      ...payloadOverrides,
    };
    try {
      const response = await submitUserTypeFilterData(payload);
      const responseData = response?.data || {};
      const listData = responseData?.records || responseData || [];
      const mappedData = (Array.isArray(listData) ? listData : []).map((item) => ({
        ...item,
        id: item?.id || item?._id || "",
      }));

      if (response) {
        setUserTypeData(mappedData);
        setPaginationMeta({
          pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
          pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
          totalPages: Number(responseData?.totalPages) || 0,
          totalRecords: Number(responseData?.totalRecords) || 0,
        });
        setRequestPayload(payload);
      } else {
        setUserTypeData([]);
      }
    } catch (e) {
      toast.error(e?.message || "Unable to load user types");
      setUserTypeData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    getUserTypeTableSchema();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getUserTypeList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize]);

  const handleSearchSubmit = async (value) => {
    const trimmedValue = value?.trim() || "";
    await getUserTypeList({
      ...requestPayload,
      pageNumber: 1,
      nameContains: trimmedValue,
    });
  };

  const handleEdit = (row) => {
    const id = String(row?.id ?? row?._id ?? "").trim();
    if (!id) {
      toast.error("Record ID not found");
      return;
    }
    navigate(`/${urlEndPoint.userTypeAddEdit}/${encodeURIComponent(id)}`);
  };

  const handleCreate = () => {
    navigate(`/${urlEndPoint.userTypeAddEdit}`);
  };

  const handlePageChange = async (page) => {
    await getUserTypeList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getUserTypeList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-3">User Type</h5>
              {userTypeActionPermissions.canCreate ? (
                <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                  <Button color="primary" type="button" onClick={handleCreate}>
                    + Create
                  </Button>
                </Col>
              ) : null}
            </div>
            <hr />

            <Row className="align-items-center g-2 mb-4">
              <Col xs="12" md="8">
                <div className="d-flex border rounded w-100 category-search-input">
                  <Input
                    type="text"
                    placeholder="Search by name"
                    className="border-0"
                    value={searchQuery}
                    onChange={(e) => {
                      const nextValue = e?.target?.value || "";
                      setSearchQuery(nextValue);
                      if (!nextValue.trim()) {
                        handleSearchSubmit("");
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchSubmit(searchQuery?.trim());
                      }
                    }}
                  />
                  <Button
                    type="button"
                    className="search-icon-button"
                    onClick={() => handleSearchSubmit(searchQuery?.trim())}
                  >
                    <i className="fa fa-search" />
                  </Button>
                </div>
              </Col>
            </Row>

            {!loading && tableMetaData?.length ? (
              <TableContainer
                columns={tableMetaData}
                data={filteredData}
                isActionRequired={hasAnyUserTypeRowAction}
                onEdit={
                  userTypeActionPermissions.canEdit ? handleEdit : undefined
                }
                isEditAccess={userTypeActionPermissions.canEdit}
                isCheckboxLeftSide={false}
                isPagination
                currentPage={paginationMeta?.pageNumber}
                rowsPerPage={paginationMeta?.pageSize}
                totalPages={paginationMeta?.totalPages}
                totalRecords={paginationMeta?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
                pageName="UserType"
                setCommonFilterModalOpen={noop}
                setCommonResetFilter={noop}
                allCommonFilter={false}
              />
            ) : (
              <Loaders />
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

UserType.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(UserType);
